import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import {APP_INITIALIZER, NgModule} from '@angular/core';
import {
    DateAdapter,
    MAT_DATE_FORMATS,
    MAT_DATE_LOCALE,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatSortModule,
    MatStepperModule,
    MatTableModule,
    MatTabsModule,
    MatTreeModule
} from '@angular/material';
import {MatMomentDateModule, MomentDateAdapter} from '@angular/material-moment-adapter';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {RouterModule} from '@angular/router';
import {FuseProgressBarModule, FuseSidebarModule, FuseThemeOptionsModule} from '@fuse/components';
import {FuseModule} from '@fuse/fuse.module';
import {FuseSharedModule} from '@fuse/shared.module';
import {TranslateModule} from '@ngx-translate/core';
import 'hammerjs';

import {AppComponent} from './app.component';
import {DISPLAY_DATE_FORMAT} from './config/date_format';
import {fuseConfig_default} from './fuse-config';
import {AuthGuard} from './guards/auth.guard';
import {LayoutModule} from './layout/layout.module';
import {HttpErrorInterceptor} from './services/httperror.interceptor.service';
import {NgImageSliderModule} from 'ng-image-slider';
import {environment} from '../environments/environment';
import {ExpenseClaimListComponent} from './main/expense-claim/expense-claim-list/expense-claim-list.component';
import {AttendanceListComponent} from './main/attendance/attendance-list/attendance-list.component';
import {NgApexchartsModule} from 'ng-apexcharts';
import {InjectableRxStompConfig, RxStompService, rxStompServiceFactory,} from '@stomp/ng2-stompjs';
import {myRxStompConfig} from './config/my-rx-stomp.config';
import {CustomDialogComponent} from '../@fuse/components/custom-dialog/custom-dialog.component';
import {CustomDialogModule} from '../@fuse/components/custom-dialog/custom-dialog.module';
import {QuillModule} from 'ngx-quill';
import {NgxDaterangepickerMd} from 'ngx-daterangepicker-material';
import {ScrollingModule} from '@angular/cdk/scrolling';
import {ColorPickerModule} from 'ngx-color-picker';
import {QRCodeModule} from 'angularx-qrcode';
import {NgxQRCodeModule} from '@techiediaries/ngx-qrcode';
import {LeafletModule} from '@asymmetrik/ngx-leaflet';
import {BaseService} from './services/base.service';
import {User} from './models/user';
import {AppConfigService} from './services/app-config.service';
import {LocationModule} from './main/location/location.module';

export function initializeApp(service: BaseService): () => Promise<any> {
    return () => {
        const user: User = JSON.parse(localStorage.getItem('user') || null);
        if (user) {
            const [module = null] = user.UserRole.ModuleList.filter(pModule => pModule.ModuleNameEnum == 'POI');
            if (module == null) {
                return Promise.resolve(null);
            } else {
                const poiPermissions = module.ResourceList.filter(pResource => pResource.ResourceEnum == 'CREATE_POI_TYPE' || pResource.ResourceEnum == 'EDIT_POI_TYPE').length == 2;
                if (poiPermissions) {
                    return service.getPoiTypeList({
                        Active: true,
                        ForProduct: true,
                        ResponseFields: 'Id,Active,Name',
                    })
                        .toPromise()
                        .catch(() => {
                            return Promise.resolve(null);
                        });
                } else {
                    return Promise.resolve(null);
                }
            }
        } else {
            return Promise.resolve(null);
        }
    };
}


export function initConfig(config: AppConfigService) {
    return () => config.load();
}

@NgModule({
    declarations: [AppComponent],
    imports: [
        BrowserModule,
        MatNativeDateModule,
        BrowserModule,
        HttpClientModule,
        BrowserAnimationsModule,
        MatDatepickerModule,
        MatInputModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatInputModule,
        MatButtonModule,
        MatDatepickerModule,
        BrowserModule,
        BrowserAnimationsModule,
        HttpClientModule,
        NgImageSliderModule,
        NgApexchartsModule,
        ScrollingModule,
        ColorPickerModule,
        QRCodeModule,
        NgxQRCodeModule,
        LocationModule,
        QuillModule.forRoot(),
        TranslateModule.forRoot(),
        NgxDaterangepickerMd.forRoot(),
        LeafletModule.forRoot(),
        RouterModule.forRoot([
            {path: 'auth', loadChildren: './main/authentication/auth.module#AuthModule'},
            {
                path: '',
                canActivate: [AuthGuard],
                children: [
                    {path: 'dashboard', loadChildren: './main/dashboard/dashboard.module#DashboardModule'},
                    {path: 'classic', loadChildren: './main/dashboard/dashboard.module#DashboardModule'},
                    {path: 'creditOption', loadChildren: './main/credit/credit.module#CreditModule'},
                    {path: 'customerReturnReason', loadChildren: './main/customerReturnReason/customerReturnReason.module#CustomerReturnReasonModule'},
                    {path: 'distributorReturnReason', loadChildren: './main/distributorReturnReason/distributorReturnReason.module#DistributorReturnReasonModule'},
                    {path: 'organization', loadChildren: './main/organization/organization.module#OrganizationModule'},
                    {path: 'bill-pending-organization', loadChildren: './main/bill-pending-organization/bill-pending-organization.module#BillPendingOrganizationModule'},
                    {path: 'market-level', loadChildren: './main/market-level/market-level.module#MarketLevelModule'},
                    {path: 'market', loadChildren: './main/market/market.module#MarketModule'},
                    {path: 'user-role', loadChildren: './main/user-role/user-role.module#UserRoleModule'},
                    {path: 'user', loadChildren: './main/user/user.module#UserModule'},
                    {path: 'notice-board', loadChildren: './main/notice-board/notice-board.module#NoticeBoardModule'},
                    {path: 'notice-board-for-others', loadChildren: './main/notice-board-for-others/notice-board-for-others.module#NoticeBoardForOthersModule'},
                    {path: 'attendance', loadChildren: './main/attendance/attendance.module#AttendanceModule'},
                    {path: 'distributor-type', loadChildren: './main/distributor-type/distributor-type.module#DistributorTypeModule'},
                    {path: 'distributor', loadChildren: './main/distributor/distributor.module#DistributorModule'},

                    {
                        path: 'distributor-account-transaction',
                        loadChildren: './main/distributor-account-transactions/distributor-account-transaction.module#DistributorAccountTransactionModule'
                    },
                    {
                        path: 'customer-account-transaction',
                        loadChildren: './main/customer-account-transactions/customer-account-transaction.module#CustomerAccountTransactionModule'
                    },

                    {path: 'distribution-centre-stock', loadChildren: './main/stock_distributor_centre/stock-distributor-centre.module#StockDistributorCentreModule'},
                    {
                        path: 'distribution-centre-stock-transaction',
                        loadChildren: './main/stock_transaction_distributor_centre/stock-transaction-distributor-centre.module#StockTransactionDistributorCentreModule'
                    },
                    {path: 'distribution-centre', loadChildren: './main/distributor-centre/distributor-centre.module#DistributorCentreModule'},

                    {path: 'distributor-user', loadChildren: './main/distributor-user/distributor-user.module#DistributorUserModule'},
                    {path: 'customer-type', loadChildren: './main/customer-type/customer-type.module#CustomerTypeModule'},
                    {path: 'customer', loadChildren: './main/customer/customer.module#CustomerModule'},
                    {path: 'product-line', loadChildren: './main/product-line/product-line.module#ProductLineModule'},
                    {path: 'product-brand', loadChildren: './main/product-brand/product-brand.module#ProductBrandModule'},
                    {path: 'product-group', loadChildren: './main/product-group/product-group.module#ProductGroupModule'},
                    {path: 'product-type', loadChildren: './main/product-type/product-type.module#ProductTypeModule'},
                    {path: 'tag', loadChildren: './main/tag/tag.module#TagModule'},
                    {path: 'product', loadChildren: './main/product/product.module#ProductModule'},
                    {path: 'distributor-order', loadChildren: './main/primary-order/primary-order.module#PrimaryOrderModule'},
                    {path: 'customer-order', loadChildren: './main/secondary-order/secondary-order.module#SecondaryOrderModule'},
                    {path: 'customer-campaign', loadChildren: './main/customer-campaign/customer-campaign.module#CustomerCampaignModule'},

                    {path: 'distributor-campaign', loadChildren: './main/distributor-campaign/distributor-campaign.module#DistributorCampaignModule'},

                    {path: 'distributor-invoice', loadChildren: './main/distributor-invoice/distributor-invoice.module#DistributorInvoiceModule'},
                    {path: 'customer-invoice', loadChildren: './main/customer-invoice/customer-invoice.module#CustomerInvoiceModule'},
                    {path: 'payment-purpose', loadChildren: './main/payment-purpose/payment-purpose.module#PaymentPurposeModule'},
                    {path: 'payment-category', loadChildren: './main/payment-category/payment-category.module#PaymentCategoryModule'},


                    {path: 'bank-account', loadChildren: './main/organization-bank-account/organization-bank-account.module#OrganizationBankAccountModule'},
                    {path: 'distributor-bank-account', loadChildren: './main/distributor-bank-account/distributor-bank-account.module#DistributorBankAccountModule'},
                    {path: 'payment', loadChildren: './main/distributor-payment/distributor-payment.module#DistributorPaymentModule'},
                    {path: 'bank', loadChildren: './main/bank/bank.module#BankModule'},
                    {path: 'customer-payment', loadChildren: './main/customer-payment/customer-payment.module#CustomerPaymentModule'},
                    {
                        path: 'distributor-delivery-challan',
                        loadChildren: './main/distributor-delivery-challan/distributor-delivery-challan.module#DistributorDeliveryChallanModule'
                    },
                    {path: 'customer-delivery-challan', loadChildren: './main/customer-delivery-challan/customer-delivery-challan.module#CustomerDeliveryChallanModule'},
                    {path: 'customer-return', loadChildren: './main/customer-return/customer-return.module#CustomerReturnModule'},
                    {path: 'distributor-return', loadChildren: './main/distributor-return/distributor-return.module#DistributorReturnModule'},
                    {path: 'distributor-return-challan', loadChildren: './main/distributor-return-challan/distributor-return-challan.module#DistributorReturnChallanModule'},
                    {path: 'customer-return-challan', loadChildren: './main/customer-return-challan/customer-return-challan.module#CustomerReturnChallanModule'},
                    {path: 'location', loadChildren: './main/location/location.module#LocationModule'},
                    {path: 'distributor-stock', loadChildren: './main/distributor-stock/distributor-stock.module#DistributorStockModule'},
                    {path: 'safety-stock', loadChildren: './main/safety-stock/safety-stock.module#SafetyStockModule'},

                    {path: 'tour-plan', loadChildren: './main/tour-plan/tour-plan.module#TourPlanModule'},
                    {path: 'target', loadChildren: './main/target/target.module#TargetModule'},
                    {path: 'target-v4', loadChildren: './main/target-v4/target-v4.module#TargetV4Module'},
                    {path: 'central-stock', loadChildren: './main/central-stock/central-stock.module#CentralStockModule'},
                    {path: 'code-generator-rule', loadChildren: './main/code-generator-rule/code-generator-rule.module#CodeGeneratorRuleModule'},
                    {path: 'income', loadChildren: './main/income/income.module#IncomeModule'},
                    {path: 'email-notification', loadChildren: './main/email-notification/email-notification.module#EmailNotificationModule'},

                    {path: 'holiday', loadChildren: './main/holiday/holiday.module#HolidayModule'},
                    {path: 'leave-type', loadChildren: './main/leave-type/leave-type.module#LeaveTypeModule'},
                    {path: 'leave-allocation', loadChildren: './main/leave-allocation/leave-allocation.module#LeaveAllocationModule'},
                    {path: 'leave-account', loadChildren: './main/leave-account/leave-account.module#LeaveAccountModule'},
                    {path: 'leave', loadChildren: './main/user-leave/user-leave.module#UserLeaveModule'},
                    {path: 'expense-type', loadChildren: './main/expense-type/expense-type.module#ExpenseTypeModule'},
                    {path: 'expense-purpose', loadChildren: './main/expense-purpose/expense-purpose.module#ExpensePurposeModule'},
                    {path: 'expense-claim', loadChildren: './main/expense-claim/expense-claim.module#ExpenseClaimModule'},
                    {path: 'ta-da', loadChildren: './main/daily-ta-da/daily-ta-da.module#DailyTaDaModule'},
                    {path: 'ta-da-claim', loadChildren: './main/daily-ta-da-claim/daily-ta-da-claim.module#DailyTaDaClaimModule'},
                    {path: 'payment-method', loadChildren: './main/payment-method/payment-method.module#PaymentMethodModule'},
                    {path: 'product-campaign', loadChildren: './main/product-campaign/product-campaign.module#ProductCampaignModule'},
                    {path: 'distributor-return-stock', loadChildren: './main/distributor-return-stock/distributor-return-stock.module#DistributorReturnStockModule'},
                    {path: 'tour-type', loadChildren: './main/tour-type/tour-type.module#TourTypeModule'},
                    {path: 'daily-shift', loadChildren: './main/daily-shift/daily-shift.module#DailyShiftModule'},
                    {path: 'report-leave', loadChildren: './main/report-leave/report-leave.module#ReportLeaveModule'},
                    {path: 'report-attendance', loadChildren: './main/report-attendance/report-attendance.module#ReportAttendanceModule'},
                    {
                        path: 'report-customer-invoice-product',
                        loadChildren: './main/report-customer-invoice-product/report-customer-invoice-product.module#ReportCustomerInvoiceProductModule'
                    },
                    {path: 'orderReport', loadChildren: './main/orderReport/orderReport.module#OrderReportModule'},
                    {path: 'report-expense-claim', loadChildren: './main/report-expense-claim/report-expense-claim.module#ReportExpenseClaimModule'},
                    {path: 'report-distributor', loadChildren: './main/report-distributor/report-distributor.module#ReportDistributorModule'},
                    {path: 'report-kpi', loadChildren: './main/report-kpi/report-kpi.module#ReportKpiModule'},
                    {path: 'report-target-achievement', loadChildren: './main/report-target-achievement/report-target-achievement.module#ReportTargetAchievementModule'},
                    {path: 'visit', loadChildren: './main/visit/visit.module#VisitModule'},
                    {path: 'engagement', loadChildren: './main/engagement/engagement.module#EngagementModule'},
                    {path: 'engagement-report', loadChildren: './main/engagement-report/engagement-report.module#EngagementReportModule'},
                    {path: 'admin-settings', loadChildren: './main/admin-settings/admin-settings.module#AdminSettingsModule'},
                    {path: 'project', loadChildren: './main/project/project.module#ProjectModule'},
                    {path: 'expense-claim-approval-rules', loadChildren: './main/expense-claim-approval-rules/expense-claim-approval-rules.module#ExpenseClaimApproveRulesModule'},
                    {path: 'project-report', loadChildren: './main/project-report/project-report.module#ProjectReportModule'},
                    {path: 'route-plan-achievement', loadChildren: './main/route-plan-achievement/route-plan-achievement.module#RoutePlanAchievementModule'},
                    {path: 'expense-sub-type', loadChildren: './main/expense-sub-type/expense-sub-type.module#ExpenseSubTypeModule'},
                    {path: 'user-wise-expense-summary', loadChildren: './main/user-wise-expense-summary/user-wise-expense-summary.module#UserWiseExpenseSummaryModule'},
                    {path: 'visit-summary-approval-rules', loadChildren: './main/visit-summary-approval-rules/visit-summary-approval.module#VisitSummaryApprovalModule'},
                    {path: 'dealer-type', loadChildren: './main/dealer-type/dealer-type.module#DealerTypeModule'},
                    {path: 'dealer', loadChildren: './main/dealer/dealer.module#DealerModule'},
                    {path: 'dealer-payment', loadChildren: './main/dealer-payment/dealer-payment.module#DealerPaymentModule'},
                    {path: 'dealer-order', loadChildren: './main/distributor-dealer-order/distributor-dealer-order.module#DistributorDealerOrderModule'},
                    {path: 'dealer-invoice', loadChildren: './main/distributor-dealer-invoice/distributor-dealer-invoice.module#DistributorDealerInvoiceModule'},
                    {path: 'dealer-account-transactions', loadChildren: './main/dealer-account-transactions/dealer-account-transactions.module#DealerAccountTransactionsModule'},
                    {path: 'dealer-delivery', loadChildren: './main/distributor-dealer-delivery/distributor-dealer-delivery.module#DistributorDealerDeliveryModule'},
                    {path: 'retailer-to-dealer-order', loadChildren: './main/retailer-to-dealer-order/retailer-to-dealer-order.module#RetailerToDealerOrderModule'},
                    {path: 'retailer-to-dealer-invoice', loadChildren: './main/retailer-to-dealer-invoice/retailer-to-dealer-invoice.module#RetailerToDealerInvoiceModule'},
                    {
                        path: 'retailer-to-dealer-delivery-challan',
                        loadChildren: './main/retailer-to-dealer-delivery/retailer-to-dealer-delivery.module#RetailerToDealerDeliveryModule'
                    },
                    {path: 'dealer-stock', loadChildren: './main/dealer-stock/dealer-stock.module#DealerStockModule'},
                    {path: 'report-retailer', loadChildren: './main/report-retailer/report-retailer.module#ReportRetailerModule'},
                    {path: 'retailer-to-dealer-campaign', loadChildren: './main/retailer-to-dealer-campaign/retailer-to-dealer-campaign.module#RetailerToDealerCampaignModule'},
                    {path: 'weekend', loadChildren: './main/weekend/weekend.module#WeekendModule'},
                    {path: 'weekend', loadChildren: './main/weekend/weekend.module#WeekendModule'},
                    {path: 'report-dealer', loadChildren: './main/report-dealer/report-dealer.module#ReportDealerModule'},
                    {path: 'report-sms', loadChildren: './main/report-sms/report-sms.module#ReportSmsModule'},
                    {path: 'report-notice', loadChildren: './main/report-notice/report-notice.module#ReportNoticeModule'},
                    {path: 'sample-requisition', loadChildren: './main/sample-requisition/sample-requisition.module#SampleRequisitionModule'},
                    {path: 'user-role-wise-visit-target', loadChildren: './main/user-role-wise-visit-target/user-role-wise-visit-target.module#UserRoleWiseVisitTargetModule'},
                    {path: 'dealer-campaign', loadChildren: './main/dealer-campaign/dealer-campaign.module#DealerCampaignModule'},
                    {path: 'noc', loadChildren: './main/noc/noc.module#NocModule'},
                    {path: 'segment', loadChildren: './main/segment/segment.module#SegmentModule'},
                    {path: 'sub-segment', loadChildren: './main/sub-segment/sub-segment.module#SubSegmentModule'},
                    {path: 'geo-hierarchy', loadChildren: './main/geo-hierarchy/geo-hierarchy.module#GeoHierarchyModule'},
                    {path: 'retailer-stock', loadChildren: './main/retailer-stock/retailer-stock.module#RetailerStockModule'},
                    {path: 'tertiary-sales', loadChildren: './main/tertiary-sales/tertiary-sales.module#TertiarySalesModule'},
                    {path: 'retailer-serial-wise-stock', loadChildren: './main/product-Serial-Info/product-serial-info.module#ProductSerialInfoModule'},
                    {path: 'linnex-tertiary-sales', loadChildren: './main/linnex-tertiary-sales/linnex-tertiary-sales.module#LinnexTertiarySalesModule'},
                    {path: 'level', loadChildren: './main/level/level.module#LevelModule'},
                    {path: 'training', loadChildren: './main/training/training.module#TrainingModule'},
                    {path: 'scheme', loadChildren: './main/scheme/scheme.module#SchemeModule'},
                    {path: 'notification-rule', loadChildren: './main/notification-rule/notification-rule.module#NotificationRuleModule'},
                    {path: 'tour-allowance-claim', loadChildren: './main/tour-allowance-claim/tour-allowance-claim.module#TourAllowanceClaimModule'},
                    {path: 'poi', loadChildren: './main/poi/poi.module#POIModule'},
                    {path: 'dsr-user', loadChildren: './main/dsr-user/dsr-user.module#DsrUserModule'},
                    {path: 'user-active-history', loadChildren: './main/user-active-history/user-active-history.module#UserActiveHistoryModule'},
                    {path: 'approval-rules', loadChildren: './main/approval-rules/approval-rules.module#ApprovalRulesModule'},
                    {path: 'lovello-fridge', loadChildren: './main/lovello-fridge/lovello-fridge.module#LovelloFridgeModule'},
                    {path: 'da-rule', loadChildren: './main/da-rule/da-rule.module#DaRuleModule'},
                    {path: 'ta-claim', loadChildren: './main/ta-claim/ta-claim.module#TaClaimModule'},
                    {path: 'stock-survey', loadChildren: './main/stock-survey/stock-survey.module#StockSurveyModule'},
                    {path: 'pjp-plan', loadChildren: './main/pjp/pjp.module#PjpModule'},
                    {path: 'route-plan', loadChildren: './main/route-plan/route-plan.module#RoutePlanModule'},
                    {path: 'fmcg-customer-invoice', loadChildren: './main/fmcg-customer-invoice/fmcg-customer-invoice.module#FmcgCustomerInvoiceModule'},
                    {path: 'retailer-sales', loadChildren: './main/retailer-sales/retailer-sales.module#RetailerSalesModule'},
                    {path: 'ims', loadChildren: './main/ims/ims.module#ImsModule'},
                    {path: 'asset', loadChildren: './main/asset/asset.module#AssetModule'},
                    {path: 'report-ims', loadChildren: './main/report-ims/report-ims.module#ReportImsModule'},
                    {path: 'order-volume-summary', loadChildren: './main/order-volume-summary/order-volume-summary.module#OrderVolumeSummaryModule'},
                    {path: 'general-return', loadChildren: './main/general-return/general-return.module#GeneralReturnModule'},

                    {path: '**', redirectTo: 'dashboard'}
                ]
            }
        ]),

        // Material moment date module
        MatMomentDateModule,

        // Material
        MatIconModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatStepperModule,
        MatCheckboxModule,
        MatTreeModule,
        MatCardModule,
        MatTableModule,
        MatPaginatorModule,
        MatSortModule,
        MatProgressSpinnerModule,
        MatTabsModule,
        MatExpansionModule,
        MatSlideToggleModule,
        MatDatepickerModule,
        MatProgressBarModule,

        // Fuse modules
        FuseModule.forRoot(fuseConfig_default),
        FuseProgressBarModule,
        FuseSharedModule,
        FuseSidebarModule,
        FuseThemeOptionsModule,
        CustomDialogModule,

        // App modules
        LayoutModule
    ],
    providers: [
        {
            provide: APP_INITIALIZER,
            useFactory: initConfig,
            deps: [AppConfigService],
            multi: true
        },
        {
            provide: InjectableRxStompConfig,
            useFactory: myRxStompConfig,
            deps: [AppConfigService]
        },
        {
            provide: RxStompService,
            useFactory: rxStompServiceFactory,
            deps: [InjectableRxStompConfig],
        },
        [
            AttendanceListComponent,
            ExpenseClaimListComponent
        ],
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorInterceptor,
            multi: true
        },
        AuthGuard,
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE]
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: DISPLAY_DATE_FORMAT
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeApp,
            deps: [BaseService],
            multi: true
        }
    ],
    entryComponents: [
        CustomDialogComponent,
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}
